import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { ENDPOINT_PATH } from '../config';

/**
 * ProtectedRoute Component
 * Protects routes that should only be accessible to specific user roles
 * 
 * @param {Object} props - Component props
 * @param {JSX.Element} props.children - The protected content to render
 * @param {string|string[]} props.requiredRole - The role(s) required to access (e.g., 'admin', 'artist', 'employee', or ['admin', 'employee'])
 * @returns {JSX.Element} - Either the protected content or a redirect
 */
const ProtectedRoute = ({ children, requiredRole = 'admin' }) => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkAuthorization = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                
                // If no user is logged in
                if (!user || !user.uid) {
                    setLoading(false);
                    return;
                }

                // Fetch user's account type from the backend
                const response = await fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + encodeURIComponent(user.uid));
                
                // Check if the response is OK
                if (!response.ok) {
                    const errorMsg = `API error: ${response.status} ${response.statusText}`;
                    console.error(errorMsg);
                    setError(errorMsg);
                    setLoading(false);
                    return;
                }
                
                const accountType = await response.json();
                console.log('Account type received:', accountType);

                // Check if the user has the required role(s)
                const accountTypeLower = accountType.toLowerCase();
                const allowedRoles = Array.isArray(requiredRole) 
                    ? requiredRole.map(role => role.toLowerCase())
                    : [requiredRole.toLowerCase()];
                
                if (allowedRoles.includes(accountTypeLower)) {
                    setIsAuthorized(true);
                }
            } catch (error) {
                console.error('Error checking authorization:', error);
                setError(error.message);
                // Don't authorize if there's an error
                setIsAuthorized(false);
            } finally {
                setLoading(false);
            }
        };

        checkAuthorization();
    }, [requiredRole]);

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column' 
            }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Verifying access...</p>
            </div>
        );
    }

    // Show error message if API call failed
    if (error) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column' 
            }}>
                <div className="alert alert-danger">
                    <h5>Unable to verify access</h5>
                    <p>{error}</p>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => window.location.href = '/'}
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    if (!isAuthorized) {
        // Redirect unauthorized users to home page
        return <Navigate to="/" replace />;
    }

    return children;
};

export default ProtectedRoute;

